import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Button, Chip, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExploreIcon from "@mui/icons-material/Explore";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EventIcon from "@mui/icons-material/Event";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { usePublicStory, useStoryMap } from "../api/storyMaps";
import { scopeByline, useStoryScope } from "../components/storyMap/useStoryScope";
import { storyMapPaths } from "../components/storyMap/storyPaths";
import {
  usePublicCommentActions,
  usePublicStoryComments,
} from "../api/storyMapComments";
import SceneComments from "../components/storyMap/SceneComments";
import {
  MetricItem,
  SceneMode,
  StoryBlock,
  StoryContent,
  StoryScene,
} from "../types/storyMap";
import { sanitizeStoryHtml } from "../components/storyMap/sanitizeStoryHtml";
import StoryMapCanvas, {
  StoryMapCanvasHandle,
} from "../components/storyMap/StoryMapCanvas";
import { webglAvailable } from "../components/storyMap/webgl";
import { useHandheldLayout } from "../components/storyMap/useHandheldLayout";
import CompareMapView from "../components/storyMap/CompareMapView";
import EmbedSceneImage from "../components/storyMap/EmbedSceneImage";
import { useSceneAnimations } from "../components/storyMap/useSceneAnimations";
import StoryLegend from "../components/storyMap/StoryLegendCard";
import WindParticleLayer from "../components/layer_components/WindParticleLayer";
import StoryFuel3DLayers from "../components/storyMap/StoryFuel3DLayers";
import WeatherPanelCard from "../components/storyMap/WeatherPanelCard";
import {
  assetsTilesByProject,
  bboxOfBoundary,
  useSceneLayerStack,
  useStorySceneSources,
} from "../components/storyMap/useStorySceneLayers";
import { useUnitSystem } from "../hooks/useUnitSystem";
import type { Map as MapboxMap } from "mapbox-gl";

const TEAL = "#00d4ad";
const SLATE = "#212b37";

// Phones: the map takes the top of the screen and the story cards scroll
// beneath it, instead of the desktop arrangement where cards float over a
// full-viewport map (which leaves nothing of the map visible on a 390px
// screen). --story-vh is the dynamic viewport height where supported, so
// mobile browser chrome sliding in and out does not leave a gap. It comes
// from --dvh100 (index.css), which is corrected for the small-screen UI zoom.
const MOBILE_MAP_FRACTION = 0.45;

const VIEWER_CSS = `
.story-scroll { --story-vh: var(--dvh100); }
@media print {
  .story-viewer-map, .scroll-hint, .no-print { display: none !important; }
  .story-scroll { height: auto !important; overflow: visible !important; background: white !important; }
  .story-content-wrap { margin-top: 0 !important; }
  .story-section { min-height: 0 !important; padding: 0 !important; page-break-inside: avoid; }
  .story-card { box-shadow: none !important; border: 1px solid #ddd; max-width: 100% !important; margin: 16px 0 !important; }
}
`;

function MetricValue({ item }: { item: MetricItem }) {
  return (
    <Box sx={{ my: 1 }}>
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", lineHeight: 1.4, display: "block" }}
      >
        {item.label}
      </Typography>
      <Typography variant="h4" component="div" sx={{ color: TEAL, fontWeight: 700 }}>
        {item.value}
        {item.units && (
          <Typography component="span" variant="subtitle1" sx={{ ml: 0.5, color: "text.secondary" }}>
            {item.units}
          </Typography>
        )}
      </Typography>
    </Box>
  );
}

function BlockView({ block }: { block: StoryBlock }) {
  switch (block.type) {
    case "text":
      return (
        <Typography
          component="div"
          variant="body1"
          sx={{ lineHeight: 1.7, "& p": { mt: 0 }, "& a": { color: TEAL } }}
          dangerouslySetInnerHTML={{ __html: sanitizeStoryHtml(block.html) }}
        />
      );
    case "heading":
      return (
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
          {block.text}
        </Typography>
      );
    case "metric":
      return <MetricValue item={block} />;
    case "metrics_row":
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {block.items.map((item, i) => (
            <MetricValue key={i} item={item} />
          ))}
        </Box>
      );
    case "image":
      return block.url ? (
        <Box sx={{ my: 1 }}>
          <Box component="img" src={block.url} alt={block.caption} sx={{ width: "100%", borderRadius: 1 }} />
          {block.caption && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {block.caption}
            </Typography>
          )}
        </Box>
      ) : null;
    default:
      // Unknown block types (e.g. legacy content) are ignored gracefully.
      return null;
  }
}

/**
 * Scrollytelling story reader (design 1d). Serves both the authed draft
 * preview (/projects/:projectId/story-maps/:storyMapId/preview) and the
 * public share link (/s/:slug). Download PDF opens the dedicated print
 * document (StoryPrintPage) in a new tab.
 * TODO v1: no frozen raster overlays for public viewers.
 */
export default function StoryViewerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug, storyMapId } = useParams<{
    slug?: string;
    storyMapId?: string;
  }>();
  const isPublic = !!slug;
  /**
   * Embedded views render the maps as flat images instead of live Mapbox GL
   * maps: Mapbox bills per Map created, so a story iframed on a busy page is
   * unbounded, unauthenticated billing on our token.
   *
   * Embedded-ness comes from the route, not from `window.self !== window.top`.
   * Frame detection misfires on anyone framing the page for a legitimate
   * reason, can be defeated by whoever is embedding you, and makes the billing
   * behaviour invisible from the server. ShareDialog generates the snippet, so
   * this URL is the one part of the arrangement we control.
   */
  const isEmbed = isPublic && location.pathname.endsWith("/embed");
  // Not a width query: a phone with Chrome's "Desktop site" on reports a
  // 980 px viewport. See useHandheldLayout.
  const isMobile = useHandheldLayout();
  // No WebGL → the same still-image treatment as an embed, instead of the
  // "Something went wrong" page the Map constructor used to throw us to.
  const liveMaps = useMemo(() => webglAvailable(), []);
  const flatMaps = isEmbed || !liveMaps;

  const publicQuery = usePublicStory(slug);
  const draftQuery = useStoryMap(storyMapId);
  const draftStory = draftQuery.data?.data ?? null;
  // Which projects the story draws from (authed); readers get it from the
  // published payload.
  const scope = useStoryScope(isPublic ? null : draftStory);

  const title = isPublic ? publicQuery.data?.title : draftQuery.data?.data.title;
  const content: StoryContent | null | undefined = isPublic
    ? publicQuery.data?.content
    : draftQuery.data?.data.content;
  const scopeLabel = isPublic
    ? scopeByline(
        publicQuery.data?.scope ?? content?.scope ?? "project",
        publicQuery.data?.scope_name ?? content?.scopeName ?? publicQuery.data?.project_name
      )
    : scopeByline(scope.kind, scope.name);
  const publishedAt = isPublic ? publicQuery.data?.published_at : null;
  const publishedPdfUrl = isPublic
    ? publicQuery.data?.pdf_url ?? null
    : draftQuery.data?.data.published_pdf_url ?? null;

  const scenes: StoryScene[] = useMemo(() => content?.scenes ?? [], [content]);

  // Reader comments: only on the published story (never the draft preview),
  // never inside an embed, and only when the author switched them on.
  const commentsOn = isPublic && !isEmbed && !!publicQuery.data?.comments_enabled;
  const commentsQuery = usePublicStoryComments(slug, commentsOn);
  const commentActions = usePublicCommentActions(slug);
  const comments = useMemo(() => commentsQuery.data ?? [], [commentsQuery.data]);
  const commentsBusy =
    commentActions.create.isPending ||
    commentActions.resolve.isPending ||
    commentActions.remove.isPending;
  const commentsError =
    commentActions.create.error?.message ||
    commentActions.resolve.error?.message ||
    commentActions.remove.error?.message ||
    null;

  const [activeIdx, setActiveIdx] = useState(-1);
  // Explore mode is opt-in per scene: even explorable scenes start
  // non-interactive so wheel over the page always scrolls the story.
  const [exploreActive, setExploreActive] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<StoryMapCanvasHandle | null>(null);
  // The sticky map instance as state, for the wind-particle DOM overlay.
  const [mapInstance, setMapInstance] = useState<MapboxMap | null>(null);
  const unitSystem = useUnitSystem();
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);

  const activeScene = activeIdx >= 0 ? scenes[activeIdx] : undefined;

  // Leaving the scene (scroll) always drops back to reading mode.
  useEffect(() => {
    setExploreActive(false);
  }, [activeIdx]);

  const mapInteractive = !!activeScene?.explorable && exploreActive;

  // Story-wide layer data sources (frozen mapData public / live endpoints
  // authed) + the active scene's stack — shared with the print page.
  const sources = useStorySceneSources({ isPublic, content, scope });
  // What the map frames before a scene camera takes over: the project's
  // boundary, or every cited project's extent.
  const boundaryFeature = sources.extentFeature;
  const boundary = boundaryFeature;
  // ----- scene modes: compare / animation take over the sticky map -----
  const activeMode: SceneMode = activeScene?.mode ?? "layers";
  // On phones the legend and weather readings move off the (small) map and
  // into the scene card, in their light print-style variant.
  const overlaysOnMap = !isMobile;
  const activeAnimations = useMemo(
    () => (activeMode === "animation" ? activeScene?.animations ?? [] : []),
    [activeMode, activeScene?.animations]
  );
  const anim = useSceneAnimations({
    animations: activeAnimations,
    defaultProjectId: sources.defaultProjectId,
    isPublic,
    // Embeds (and browsers without WebGL) show a still of the final frame;
    // no frame data to fetch, no playback clock to run.
    enabled: activeMode === "animation" && !flatMaps,
    avoidPosition:
      overlaysOnMap && activeScene?.legend?.enabled
        ? activeScene.legend.position ?? "bottom-right"
        : null,
    assetsTilesUrl: sources.assetsTilesUrl,
    assetsTilesUrlByProject: assetsTilesByProject(sources),
    getMap: () => canvasRef.current?.getMap() ?? null,
  });
  const { layers: sceneLayers, legendRows, wind, weatherPanel, fuel3d } = useSceneLayerStack({
    scene: activeScene,
    sources,
    isPublic,
    weatherElapsedSeconds: activeMode === "animation" ? anim.elapsedSeconds : null,
    unitSystem,
  });
  const windBounds = useMemo(() => bboxOfBoundary(boundaryFeature), [boundaryFeature]);
  const stickyLayers = useMemo(
    () =>
      activeMode === "animation" && anim.layers.length > 0
        ? [...sceneLayers, ...anim.layers]
        : sceneLayers,
    [activeMode, sceneLayers, anim.layers]
  );

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || scenes.length === 0) return;
    // A scene becomes active when it crosses a thin band of the scroll
    // container: mid-screen on desktop; on phones the band sits in the
    // lower part of the screen, below the pinned map.
    const rootMargin = isMobile ? "-70% 0px -20% 0px" : "-45% 0px -45% 0px";
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveIdx(Number((entry.target as HTMLElement).dataset.sceneIdx));
          }
        });
      },
      { root, rootMargin, threshold: 0 }
    );
    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [scenes.length, isMobile]);

  const viewportHeight = isPublic
    ? "var(--story-vh)"
    : "calc(var(--story-vh) - var(--topbar-h))";
  // Phones: the map is the top part of the screen, the cards get the rest.
  const mapHeight = isMobile
    ? `calc(${viewportHeight} * ${MOBILE_MAP_FRACTION})`
    : viewportHeight;
  const panelHeight = isMobile
    ? `calc(${viewportHeight} * ${1 - MOBILE_MAP_FRACTION})`
    : viewportHeight;
  if (isPublic && publicQuery.isError) {
    return (
      <Box
        sx={{
          height: "var(--vh100)",
          bgcolor: SLATE,
          color: "white",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <Typography variant="h5">This story is not available.</Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
          It may have been unpublished, or the link is incorrect.
        </Typography>
      </Box>
    );
  }

  if (!content) {
    return (
      <Box
        sx={{
          height: viewportHeight,
          bgcolor: SLATE,
          color: "rgba(255,255,255,0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Loading story…</Typography>
      </Box>
    );
  }

  const author = [content.authorName, content.authorRole].filter(Boolean).join(", ");
  const frozenDate = publishedAt || content.publishedAt;
  const formatDate = (iso: string | null | undefined) =>
    iso
      ? new Date(iso).toLocaleDateString(undefined, {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "";

  const card = (children: React.ReactNode, key?: string | number) => (
    <Box
      key={key}
      className="story-card"
      sx={{
        pointerEvents: "auto",
        // Stacked layout: full width on a phone, but capped so a tablet —
        // or a phone claiming a 980 px desktop viewport — does not set
        // prose in one 800 px line.
        maxWidth: isMobile ? 680 : 460,
        mx: isMobile ? "auto" : 0,
        width: "100%",
        bgcolor: "rgba(255,255,255,0.96)",
        color: "#1c2530",
        borderRadius: 2,
        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        p: isMobile ? 2 : 3,
        // The app shell centres all text (a #root rule inherited from the
        // Vite template), which turns a scene's paragraphs into centred
        // ragged blocks — unreadable at phone width. Prose reads left.
        textAlign: "left",
      }}
    >
      {children}
    </Box>
  );

  return (
    <Box
      className="story-scroll"
      ref={scrollRef}
      sx={{ height: viewportHeight, overflowY: "auto", bgcolor: SLATE }}
    >
      <style>{VIEWER_CSS}</style>

      {/* Preview banner */}
      {!isPublic && (
        <Box
          className="no-print"
          sx={{
            position: "fixed",
            top: "var(--topbar-h)",
            left: 0,
            right: 0,
            zIndex: 10,
            bgcolor: "#b26a00",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Preview — draft data, not yet frozen
          </Typography>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => draftStory && navigate(storyMapPaths.builder(draftStory))}
            sx={{ color: "white", textTransform: "none" }}
          >
            Back to builder
          </Button>
        </Box>
      )}

      {/* Sticky map: full viewport behind the scroll sections on desktop,
          the top of the screen with the sections beneath it on phones */}
      <Box
        className="story-viewer-map"
        sx={{
          position: "sticky",
          top: 0,
          height: mapHeight,
          zIndex: isMobile ? 1 : "auto",
          bgcolor: SLATE,
        }}
      >
        {flatMaps ? (
          <EmbedSceneImage
            scene={activeScene ?? scenes[0]}
            sceneImages={content.sceneImages}
            storyUrl={`${window.location.origin}/s/${slug}`}
            note={
              !liveMaps
                ? "Interactive maps aren't available in this browser — showing a still image"
                : null
            }
            showOpenLink={isEmbed}
          />
        ) : activeMode === "compare" && activeScene?.compare ? (
          <CompareMapView
            compare={activeScene.compare}
            camera={activeScene.camera}
            isPublic={isPublic}
            sources={sources}
            fitTo={boundaryFeature}
            interactive={mapInteractive}
            legend={overlaysOnMap ? activeScene.legend : null}
          />
        ) : (
          <StoryMapCanvas
            ref={canvasRef}
            layers={stickyLayers}
            fitTo={boundary}
            initialCamera={scenes[0]?.camera ?? null}
            initialBounds={boundary}
            camera={activeScene?.camera ?? null}
            transition={activeScene?.transition ?? "fly"}
            interactive={mapInteractive}
            onMapReady={setMapInstance}
          />
        )}
        {!flatMaps && overlaysOnMap && activeMode !== "compare" && weatherPanel && (
          <WeatherPanelCard
            data={weatherPanel}
            side={weatherPanel.side}
            unitSystem={unitSystem}
          />
        )}
        {!flatMaps && activeMode !== "compare" && (
          <WindParticleLayer
            map={mapInstance}
            isActive={!!wind && !!mapInstance}
            windSpeed={wind?.windSpeed ?? 0}
            windDirection={wind?.windDirection ?? 0}
            field={wind?.field ?? null}
            bounds={windBounds ?? { top: 0, bottom: 0, left: 0, right: 0 }}
          />
        )}
        {!flatMaps && activeMode !== "compare" && (
          <StoryFuel3DLayers map={mapInstance} items={fuel3d} crownsEnabled={!isPublic} />
        )}
        {!flatMaps && activeMode === "animation" && anim.overlay}
        {!flatMaps && activeMode === "animation" && anim.controls}
        {!flatMaps && activeMode === "animation" && anim.legend}
        {!flatMaps &&
          overlaysOnMap &&
          activeMode !== "compare" &&
          activeScene?.legend?.enabled && (
            <StoryLegend rows={legendRows} legend={activeScene.legend} />
          )}
        {!flatMaps && activeScene?.explorable && activeMode !== "animation" && (
          <Chip
            className="scroll-hint"
            clickable
            onClick={() => setExploreActive((active) => !active)}
            icon={
              exploreActive ? (
                <MenuBookIcon sx={{ color: `${TEAL} !important` }} />
              ) : (
                <ExploreIcon sx={{ color: `${TEAL} !important` }} />
              )
            }
            label={
              exploreActive
                ? "Resume reading"
                : isMobile
                ? "Explore this map — tap to pan & zoom"
                : "Explore this map — click to pan & zoom"
            }
            sx={{
              position: "absolute",
              // Phones: the map is short and the Mapbox wordmark and
              // attribution own its bottom edge, so the cue sits at the top.
              ...(isMobile ? { top: 12 } : { bottom: 24 }),
              left: "50%",
              transform: "translateX(-50%)",
              maxWidth: "calc(100% - 24px)",
              bgcolor: "rgba(33,43,55,0.85)",
              color: "white",
              "&:hover": { bgcolor: "rgba(33,43,55,0.95)" },
            }}
          />
        )}
      </Box>

      {/* Scroll content: overlaying the map on desktop, beneath it on phones */}
      <Box
        className="story-content-wrap"
        sx={{
          position: "relative",
          mt: isMobile ? 0 : `calc(-1 * ${viewportHeight})`,
          pointerEvents: "none",
        }}
      >
        {/* Hero */}
        <Box
          className="story-section"
          sx={{
            minHeight: panelHeight,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
            py: isMobile ? 3 : 0,
            position: "relative",
          }}
        >
          <Box
            sx={{
              pointerEvents: "auto",
              maxWidth: 760,
              // Translucent backdrop so the title reads over any map imagery.
              bgcolor: "rgba(33,43,55,0.62)",
              backdropFilter: "blur(6px)",
              borderRadius: 3,
              px: { xs: 3, md: 6 },
              py: { xs: 4, md: 5 },
              boxShadow: "0 8px 40px rgba(0,0,0,0.35)",
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="FiSci"
              sx={{ height: { xs: 40, sm: 56 }, display: "block", mx: "auto", mb: 1.5 }}
            />
            <Typography
              variant="overline"
              sx={{ color: TEAL, letterSpacing: 4, fontWeight: 700, display: "block" }}
            >
              FiSci Mitigate
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mb: 2 }}>
              {isPublic || frozenDate
                ? `Story Map · Published ${formatDate(frozenDate)}`
                : "Story Map · Draft preview"}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: "white",
                fontWeight: 700,
                fontSize: { xs: "2rem", md: "3.2rem" },
                textShadow: "0 2px 16px rgba(0,0,0,0.6)",
              }}
            >
              {title}
            </Typography>
            {content.subtitle && (
              <Typography
                variant="h6"
                sx={{ color: "rgba(255,255,255,0.85)", mt: 2, fontWeight: 400 }}
              >
                {content.subtitle}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 3 }}>
              {[scopeLabel, author].filter(Boolean).join(" · ")}
            </Typography>
          </Box>
          {/* Scroll cue: a solid pill (the satellite basemap swallowed the
              old bare caption), clickable to advance to the first scene. */}
          <Box
            className="scroll-hint"
            role="button"
            tabIndex={0}
            onClick={() =>
              sectionRefs.current[0]?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                sectionRefs.current[0]?.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
            sx={{
              // Pinned to the hero's foot on desktop; on phones the hero is
              // the short panel under the map, so the cue follows the text.
              position: isMobile ? "relative" : "absolute",
              bottom: isMobile ? "auto" : 28,
              mt: isMobile ? 3 : 0,
              pointerEvents: "auto",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.25,
              px: 2.5,
              py: 1,
              borderRadius: 999,
              bgcolor: "rgba(33,43,55,0.85)",
              backdropFilter: "blur(6px)",
              border: `1px solid ${TEAL}`,
              boxShadow: "0 6px 24px rgba(0,0,0,0.45)",
              color: "white",
              "&:hover": { bgcolor: "rgba(33,43,55,0.97)" },
              "@keyframes storyBounce": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(6px)" },
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                letterSpacing: 3,
                fontWeight: 700,
                color: "white",
                textShadow: "0 1px 6px rgba(0,0,0,0.6)",
              }}
            >
              SCROLL
            </Typography>
            <KeyboardArrowDownIcon
              sx={{ color: TEAL, fontSize: 28, animation: "storyBounce 1.6s infinite" }}
            />
          </Box>
        </Box>

        {/* Scenes */}
        {scenes.map((scene, i) => (
          <Box
            key={scene.id}
            data-scene-idx={i}
            ref={(el: HTMLElement | null) => {
              sectionRefs.current[i] = el;
            }}
            className="story-section"
            sx={{
              minHeight: panelHeight,
              display: "flex",
              alignItems: isMobile ? "flex-start" : "center",
              px: { xs: 2, md: 10 },
              py: isMobile ? 2 : 6,
            }}
          >
            {card(
              <>
                {scene.kicker && (
                  <Typography
                    variant="overline"
                    sx={{ color: TEAL, fontWeight: 700, letterSpacing: 1.5 }}
                  >
                    {scene.kicker}
                  </Typography>
                )}
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {scene.name}
                </Typography>
                {(scene.date || scene.tilesStale || content.mapDataStale) && (
                  <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                    {scene.date && (
                      <Chip
                        size="small"
                        icon={<EventIcon />}
                        label={scene.date}
                        sx={{ bgcolor: "rgba(33,43,55,0.08)" }}
                      />
                    )}
                    {scene.tilesStale && (
                      <Chip
                        size="small"
                        color="warning"
                        icon={<WarningAmberIcon />}
                        label="Some map layers couldn't be frozen"
                      />
                    )}
                    {content.mapDataStale && (
                      <Chip
                        size="small"
                        color="warning"
                        icon={<WarningAmberIcon />}
                        label="Boundary and treatment geometry couldn't be frozen"
                      />
                    )}
                  </Box>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {/* `content` is free-form JSON that any client can PATCH, and
                      this page is public — a scene without blocks must not take
                      the whole story down. */}
                  {(scene.blocks ?? []).map((block) => (
                    <BlockView key={block.id} block={block} />
                  ))}
                </Box>
                {commentsOn && (
                  <SceneComments
                    sceneId={scene.id}
                    comments={comments}
                    viewerName={publicQuery.data?.viewer?.name ?? null}
                    isOrgMember={!!publicQuery.data?.viewer?.is_org_member}
                    busy={commentsBusy}
                    error={commentsError}
                    onCreate={(input) => commentActions.create.mutateAsync(input)}
                    onResolve={(id, resolved) =>
                      commentActions.resolve.mutateAsync({ id, resolved })
                    }
                    onDelete={(id) => commentActions.remove.mutateAsync(id)}
                  />
                )}
                {/* Phones: legend + weather readings live in the card (the
                    map is too small to float them on). Only the active
                    scene has them computed, and only live maps draw layers. */}
                {!overlaysOnMap && !flatMaps && i === activeIdx && (
                  <>
                    {scene.legend?.enabled &&
                      legendRows.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <StoryLegend
                            rows={legendRows}
                            legend={scene.legend}
                            variant="light"
                            overlay={false}
                          />
                        </Box>
                      )}
                    {scene.mode !== "compare" && weatherPanel && (
                      <Box sx={{ mt: 2 }}>
                        <WeatherPanelCard
                          data={weatherPanel}
                          unitSystem={unitSystem}
                          variant="light"
                          overlay={false}
                        />
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        ))}

        {/* Footer */}
        <Box
          className="story-section"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 8,
          }}
        >
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
            Produced with FiSci Mitigate
            {frozenDate ? ` · Data frozen at publish, ${formatDate(frozenDate)}` : ""}
          </Typography>
          {!isEmbed && (
          <Button
            className="no-print"
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={() =>
              window.open(
                // The PDF rendered once at publish, when there is one: building
                // it here costs a Mapbox map load per scene, in every reader's
                // browser, every time. The print page stays the fallback for
                // drafts, previews and stories published before that existed.
                publishedPdfUrl ??
                  (isPublic
                    ? `/s/${slug}/print`
                    : draftStory
                      ? storyMapPaths.print(draftStory)
                      : `/story-maps/${storyMapId}/print`),
                "_blank",
                "noopener"
              )
            }
            sx={{
              pointerEvents: "auto",
              color: TEAL,
              borderColor: TEAL,
              textTransform: "none",
              "&:hover": { borderColor: "#00bd9a", color: "#00bd9a" },
            }}
          >
            Download PDF
          </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
